import React, {
  createContext,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch } from 'react-redux';

import { manualSync } from 'loot-core/client/actions';
import * as queries from 'loot-core/src/client/queries';
import { listen } from 'loot-core/src/platform/client/fetch';
import { type LocalPrefs } from 'loot-core/src/types/prefs';

import { useAuth } from '../hooks/useAuth';
import { useIsTablet } from '../hooks/useIsTablet';
import { useLocalPref } from '../hooks/useLocalPref';
import { useSync } from '../hooks/useSync';
import { UncategorizedTransactions } from '../icons/v0';
import { SvgAlertTriangle, SvgViewHide, SvgViewShow } from '../icons/v2';
import { useResponsive } from '../ResponsiveProvider';
import { type CSSProperties, styles, theme } from '../style';

import { AnimatedRefresh } from './AnimatedRefresh';
import { Button } from './common/Button';
import { Link } from './common/Link';
import { Text } from './common/Text';
import { View } from './common/View';
import { useSheetValue } from './spreadsheet/useSheetValue';
import { UnreviewedTransactions } from '../icons/v2/UnreviewedTransactions';

export const SWITCH_BUDGET_MESSAGE_TYPE = 'budget/switch-type';

type SwitchBudgetTypeMessage = {
  type: typeof SWITCH_BUDGET_MESSAGE_TYPE;
  payload: {
    newBudgetType: LocalPrefs['budgetType'];
  };
};
export type TitlebarMessage = SwitchBudgetTypeMessage;

type Listener = (msg: TitlebarMessage) => void;
export type TitlebarContextValue = {
  sendEvent: (msg: TitlebarMessage) => void;
  subscribe: (listener: Listener) => () => void;
};

export const TitlebarContext = createContext<TitlebarContextValue>({
  sendEvent() {
    throw new Error('TitlebarContext not initialized');
  },
  subscribe() {
    throw new Error('TitlebarContext not initialized');
  },
});

type TitlebarProviderProps = {
  children?: ReactNode;
};

export function TitlebarProvider({ children }: TitlebarProviderProps) {
  const listeners = useRef<Listener[]>([]);

  function sendEvent(msg: TitlebarMessage) {
    listeners.current.forEach(func => func(msg));
  }

  function subscribe(listener: Listener) {
    listeners.current.push(listener);
    return () =>
      (listeners.current = listeners.current.filter(func => func !== listener));
  }

  return (
    <TitlebarContext.Provider value={{ sendEvent, subscribe }}>
      {children}
    </TitlebarContext.Provider>
  );
}

function UncategorizedButton() {
  const uncategorizedCount: number | null = useSheetValue(
    queries.uncategorizedCount(),
  );
  const unreviewedCount: number | null = useSheetValue(
    queries.unreviewedCount(),
  );
  const totalCount = uncategorizedCount + unreviewedCount;
  const hasPipe = uncategorizedCount > 0 && unreviewedCount > 0;
  const isTablet = useIsTablet();
  if (totalCount === null || totalCount <= 0) {
    return null;
  }

  return (
    <>
      {uncategorizedCount > 0 && (
        <Link
          variant="button"
          type="bare"
          to="/accounts/uncategorized"
          style={{
            gap: 3,
            color: theme.errorText,
          }}
          {...(isTablet
            ? {
                title: `${uncategorizedCount} ${`uncategorized`}`,
              }
            : {})}
        >
          {uncategorizedCount}
          {'  '}
          {isTablet ? (
            <UncategorizedTransactions fill={theme.errorText} />
          ) : (
            `uncategorized`
          )}
        </Link>
      )}
      {hasPipe ? <>|</> : ''}
      {unreviewedCount > 0 && (
        <Link
          variant="button"
          type="bare"
          to="/accounts/unreviewed"
          style={{
            gap: 3,
            color: theme.gsOrange700,
          }}
          {...(isTablet
            ? {
                title: `${unreviewedCount} ${`unreviewed`}`,
              }
            : {})}
        >
          {unreviewedCount}
          {'  '}
          {isTablet ? (
            <UnreviewedTransactions fill={theme.gsOrange700} style={{ marginLeft: 2 }} />
          ) : (
            `unreviewed`
          )}
        </Link>
      )}
    </>
  );
}

type PrivacyButtonProps = {
  style?: CSSProperties;
};

function PrivacyButton({ style }: PrivacyButtonProps) {
  const [isPrivacyEnabled, setPrivacyEnabledPref] =
    useLocalPref('isPrivacyEnabled');

  const privacyIconStyle = { width: 15, height: 15 };

  return (
    <Button
      type="bare"
      aria-label={`${isPrivacyEnabled ? 'Disable' : 'Enable'} privacy mode`}
      onClick={() => setPrivacyEnabledPref(!isPrivacyEnabled)}
      style={style}
    >
      {isPrivacyEnabled ? (
        <SvgViewHide style={privacyIconStyle} />
      ) : (
        <SvgViewShow style={privacyIconStyle} />
      )}
    </Button>
  );
}

type SyncButtonProps = {
  style?: CSSProperties;
  isMobile?: boolean;
};

export function SyncButton({ style, isMobile = false }: SyncButtonProps) {
  const [cloudFileId] = useLocalPref('cloudFileId');
  const dispatch = useDispatch();
  const { smartSync } = useSync();
  const { billingStatus } = useAuth();

  const [syncing, setSyncing] = useState(false);
  const [syncState, setSyncState] = useState<
    null | 'offline' | 'local' | 'disabled' | 'error'
  >(null);

  useEffect(() => {
    const unlisten = listen('sync-event', ({ type, subtype, syncDisabled }) => {
      if (type === 'start') {
        setSyncing(true);
        setSyncState(null);
      } else {
        // Give the layout some time to apply the starting animation
        // so we always finish it correctly even if it's almost
        // instant
        setTimeout(() => {
          setSyncing(false);
        }, 200);
      }

      if (type === 'error') {
        // Use the offline state if either there is a network error or
        // if this file isn't a "cloud file". You can't sync a local
        // file.
        if (subtype === 'network') {
          setSyncState('offline');
        } else if (!cloudFileId) {
          setSyncState('local');
        } else {
          setSyncState('error');
        }
      } else if (type === 'success') {
        setSyncState(syncDisabled ? 'disabled' : null);
      }
    });

    return unlisten;
  }, []);

  const mobileColor = !billingStatus?.isActive
    ? theme.tableTextLight
    : syncState === 'error'
      ? theme.errorText
      : syncState === 'disabled' ||
          syncState === 'offline' ||
          syncState === 'local'
        ? theme.mobileHeaderTextSubdued
        : theme.mobileHeaderText;
  const desktopColor = !billingStatus?.isActive
    ? theme.tableTextLight
    : syncState === 'error'
      ? theme.errorTextDark
      : syncState === 'disabled' ||
          syncState === 'offline' ||
          syncState === 'local'
        ? theme.tableTextInactive
        : theme.buttonNormalBorder;

  const activeStyle = isMobile
    ? {
        color: mobileColor,
      }
    : {};

  const hoveredStyle = isMobile
    ? {
        color: mobileColor,
        background: theme.mobileHeaderTextHover,
      }
    : {};

  const mobileIconStyle = {
    color: mobileColor,
    justifyContent: 'center',
    margin: 10,
    paddingLeft: 5,
    paddingRight: 3,
  };

  const mobileTextStyle = {
    ...styles.text,
    fontWeight: 500,
    marginLeft: 2,
    marginRight: 5,
  };

  useHotkeys(
    'ctrl+s, cmd+s, meta+s',
    () => smartSync(),
    {
      enableOnFormTags: true,
      preventDefault: true,
      scopes: ['app'],
    },
    [smartSync],
  );

  function onSyncClicked() {
    if (syncState !== 'disabled') {
      dispatch(manualSync());
    }
  }

  return (
    <Button
      type="bare"
      aria-label="Sync"
      style={
        isMobile
          ? {
              ...style,
              WebkitAppRegion: 'none',
              ...mobileIconStyle,
            }
          : {
              ...style,
              WebkitAppRegion: 'none',
              color: desktopColor,
            }
      }
      hoveredStyle={hoveredStyle}
      activeStyle={activeStyle}
      onClick={onSyncClicked}
    >
      {isMobile ? (
        syncState === 'error' ? (
          <SvgAlertTriangle width={14} height={14} />
        ) : (
          <AnimatedRefresh width={18} height={18} animating={syncing} />
        )
      ) : syncState === 'error' ? (
        <SvgAlertTriangle width={13} />
      ) : (
        <AnimatedRefresh animating={syncing} />
      )}
      <Text style={isMobile ? { ...mobileTextStyle } : { marginLeft: 3 }}>
        {syncState === 'disabled'
          ? 'Disabled'
          : syncState === 'offline'
            ? 'Offline'
            : 'Sync'}
      </Text>
    </Button>
  );
}

export function HelpButton() {
  const onHelpClick = () => {
    // docs below
    // https://www.chatwoot.com/hc/user-guide/articles/1677587234-how-to-send-additional-user-information-to-chatwoot-using-sdk
    const chatwoot = (window as any).$chatwoot;
    chatwoot.toggle();
  };

  return (
    <>
      <Button
        type="bare"
        aria-label="Help"
        style={{ fontWeight: 500, marginLeft: 10 }}
        onClick={onHelpClick}
      >
        <Text>Help</Text>
      </Button>
    </>
  );
}

type TitlebarProps = {
  style?: CSSProperties;
};

export function Titlebar({ style }: TitlebarProps) {
  const { isAuthenticated } = useAuth();
  const { isNarrowWidth, atLeastMediumWidth } = useResponsive();

  return isNarrowWidth ? null : (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: '0 15px',
        paddingLeft: '0',
      }}
    >
      <View style={{ flex: 1 }} />
      <UncategorizedButton />
      {/*{isDevelopmentEnvironment() && !Platform.isPlaywright && (*/}
      {/*  <ThemeSelector style={{ marginLeft: 10 }} />*/}
      {/*)}*/}
      {atLeastMediumWidth && <PrivacyButton style={{ marginLeft: 10 }} />}
      {isAuthenticated && (
        <SyncButton style={{ marginLeft: 10, fontWeight: 500 }} />
      )}
      {/*<HelpButton />*/}
    </View>
  );
}
